<script lang="ts" module>
	/**
	 * `Icon` — the app's line icons. Own work, drawn on a 24-unit grid with a
	 * 1.75 stroke, so there is no icon font to download, no third-party licence
	 * to record, and nothing for `font-src 'self'` to block.
	 *
	 * DECORATIVE BY DEFAULT: every icon sits beside a visible text label, so it is
	 * `aria-hidden`. A control that is ONLY an icon must pass `label`, which turns
	 * it into an image with an accessible name.
	 *
	 * FILLED, FOR A SELECTED STATE (the active tab). `filled` draws the house
	 * path filled AND still stroked, so the silhouette is the outline's to the
	 * pixel. Only the glyphs in FILLED have a filled form; any other name ignores
	 * the prop. An inner stroke that would vanish into the fill (the book's
	 * spine, the clock's hands) is KNOCKED OUT: drawn as its own stroke in
	 * `var(--icon-knockout, currentColor)`, which the parent sets to the colour
	 * behind the glyph. Without it the knockout is the fill's colour, i.e. a
	 * solid shape.
	 */
	export type IconName =
		| 'home'
		| 'book'
		| 'clock'
		| 'notes'
		| 'sun'
		| 'moon'
		| 'beads'
		| 'compass'
		| 'search'
		| 'settings'
		| 'check'
		| 'circle'
		| 'info'
		| 'chevron'
		| 'location'
		| 'sunrise'
		| 'reset'
		| 'screen'
		| 'highlight'
		| 'bookmark'
		| 'copy'
		| 'play'
		| 'pause'
		| 'prev'
		| 'next'
		| 'stop'
		| 'sliders'
		| 'plus'
		| 'words'
		| 'sound'
		| 'share'
		| 'add-square';

	const PATHS: Record<IconName, string> = {
		home: 'M4 11 12 4l8 7v8a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z',
		book: 'M12 6.5C10.5 5.3 8.2 4.8 5 5v13c3.2-.2 5.5.3 7 1.5 1.5-1.2 3.8-1.7 7-1.5V5c-3.2-.2-5.5.3-7 1.5zm0 0V19.5',
		clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0-13v4.5l3 2',
		notes: 'M7 3h8l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm8 0v4h4M9 12h7M9 16h5',
		sun: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0-13v2m0 14v2M3 12h2m14 0h2M5.6 5.6 7 7m10 10 1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4',
		moon: 'M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z',
		beads:
			'M12 4.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM17.3 6.7a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM19.5 12a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM6.7 6.7a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM4.5 12a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM12 13v4m-2 0h4l-2 5-2-5z',
		compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm3.5-12.5-2 5-5 2 2-5z',
		search: 'M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM20 20l-4.8-4.8',
		settings:
			'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7.4-3a7.4 7.4 0 0 0-.1-1.3l2-1.5-2-3.4-2.3 1a7.5 7.5 0 0 0-2.2-1.3L14.5 3h-4l-.4 2.5a7.5 7.5 0 0 0-2.2 1.3l-2.3-1-2 3.4 2 1.5a7.4 7.4 0 0 0 0 2.6l-2 1.5 2 3.4 2.3-1a7.5 7.5 0 0 0 2.2 1.3l.4 2.5h4l.4-2.5a7.5 7.5 0 0 0 2.2-1.3l2.3 1 2-3.4-2-1.5c.1-.4.1-.9.1-1.3z',
		check: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm-4-9 2.8 2.8L16.5 9',
		circle: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
		info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0-10v5m0-8.2v.2',
		chevron: 'M15 6l-6 6 6 6',
		location:
			'M12 21s-7-6.2-7-11.5a7 7 0 1 1 14 0C19 14.8 12 21 12 21zm0-9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
		sunrise: 'M3 18h18M7 18a5 5 0 0 1 10 0M12 4v4m-6.4 1.6L7 11m10 0 1.4-1.4M9 6l3-2 3 2',
		reset: 'M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4',
		screen: 'M7 3h10a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm4 15h2',
		highlight: 'M14 5l5 5-8 8H6v-5zm-2 2 5 5M4 21h9',
		bookmark: 'M7 4h10a1 1 0 0 1 1 1v15l-6-4-6 4V5a1 1 0 0 1 1-1z',
		copy: 'M9 9h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1zM5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1',
		/* The media glyphs point the LTR way, as drawn everywhere; a caller in
		   this RTL document mirrors them where direction carries meaning. */
		play: 'M8 5v14l11-7z',
		pause: 'M9 5v14m6-14v14',
		prev: 'M6 5v14M18 5v14L8 12z',
		next: 'M18 5v14M6 5v14l10-7z',
		stop: 'M7 6h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
		sliders:
			'M4 7h9m4 0h3M4 17h3m4 0h9M15 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-6 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
		plus: 'M12 5v14M5 12h14',
		words: 'M4 7V5h2m12 0h2v2m0 10v2h-2M6 19H4v-2m4-7h8m-8 4h5',
		sound: 'M4 9.5h3.5L12 6v12l-4.5-3.5H4zM15.5 9.5a3.5 3.5 0 0 1 0 5M18 7a7 7 0 0 1 0 10',
		/* iOS's share glyph (box, arrow out) and "Add to Home Screen" (plus in a
		   square), drawn to match the install steps on iPhone. */
		share:
			'M12 15V4m0 0L8.5 7.5M12 4l3.5 3.5M8 10H6a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1h-2',
		'add-square':
			'M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm7 4.5v7m-3.5-3.5h7'
	};

	/** The filled forms: `fill` is the closed silhouette, `knock` the inner
	 *  stroke drawn over it in the knockout colour. */
	const FILLED: Partial<Record<IconName, { fill: string; knock?: string }>> = {
		home: { fill: PATHS.home },
		bookmark: { fill: PATHS.bookmark },
		// PATHS.book without its spine, which becomes the knockout.
		book: {
			fill: 'M12 6.5C10.5 5.3 8.2 4.8 5 5v13c3.2-.2 5.5.3 7 1.5 1.5-1.2 3.8-1.7 7-1.5V5c-3.2-.2-5.5.3-7 1.5z',
			knock: 'M12 6.5V19.5'
		},
		// The clock's face is the circle glyph; its hands become the knockout.
		clock: { fill: PATHS.circle, knock: 'M12 8v4.5l3 2' }
	};
</script>

<script lang="ts">
	interface Props {
		name: IconName;
		/** Size in em, so the icon follows the text beside it. */
		size?: number;
		/** Only for an icon-only control: gives the icon an accessible name. */
		label?: string;
		/** Draw the filled form, where FILLED has one (a selected state). */
		filled?: boolean;
	}

	let { name, size = 1.25, label, filled = false }: Props = $props();

	const shape = $derived(filled ? FILLED[name] : undefined);
</script>

<svg
	class="icon"
	viewBox="0 0 24 24"
	width="{size}em"
	height="{size}em"
	fill="none"
	stroke="currentColor"
	stroke-width="1.75"
	stroke-linecap="round"
	stroke-linejoin="round"
	role={label ? 'img' : undefined}
	aria-label={label}
	aria-hidden={label ? undefined : 'true'}
	focusable="false"
>
	{#if shape}
		<path d={shape.fill} fill="currentColor" />
		{#if shape.knock}<path class="knock" d={shape.knock} />{/if}
	{:else}
		<path d={PATHS[name]} />
	{/if}
</svg>

<style>
	.icon {
		flex: none;
		display: inline-block;
		vertical-align: -0.2em;
	}

	.knock {
		stroke: var(--icon-knockout, currentColor);
	}
</style>
